import { useEffect, useRef } from "react";

const SITE_UUID = "a280d70d-6c93-4157-8f10-4ddf9e9085af";
const LOADER_SRC = "https://cdn.hsptb.com/direct-booking-widget/widget-loader.prod.js";

/**
 * Hospitable's current direct-booking widget (JS loader, replaces the retired
 * static iframe embed). The loader reads its config from data attributes on its
 * own <script> tag and inserts an auto-resizing booking iframe as a sibling of
 * that tag — so we inject a fresh script per property and clear the container
 * on cleanup, which removes both script and iframe.
 * NOTE: the loader renders ONE widget per page; PropertyDetail is the only
 * place this is used.
 */
export const HospitableWidget = ({ propertyId, propertyName }) => {
  const ref = useRef(null);
  const containerId = `hospitable-widget-${propertyId}`;

  useEffect(() => {
    const container = ref.current;
    if (!container) return undefined;
    // Defer one tick so React StrictMode's dev double-mount cancels the first
    // injection: the loader renders only the FIRST script it executes (in-flight
    // promise guard + booking-iframe dedupe), so a detached first script would
    // swallow the render. data-container makes the iframe land in our div.
    const timer = setTimeout(() => {
      const s = document.createElement("script");
      s.src = LOADER_SRC;
      s.setAttribute("data-site-uuid", SITE_UUID);
      s.setAttribute("data-property-id", propertyId);
      s.setAttribute("data-theme", "multi");
      s.setAttribute("data-container", containerId);
      container.appendChild(s);
    }, 0);
    return () => {
      clearTimeout(timer);
      container.innerHTML = "";
    };
  }, [propertyId, containerId]);

  return (
    <div
      ref={ref}
      id={containerId}
      data-testid="hospitable-widget"
      data-property-id={propertyId}
      aria-label={`Booking calendar for ${propertyName}`}
    />
  );
};
